import { ArrayUtils } from 'ts/commons/ArrayUtils';
import { StringUtils } from 'ts/commons/StringUtils';

/** Describes a single partition. */
export type Partition = {
	/** The full name of the partition including the name of the label (if applicable). */
	partition: string;
	/** The readable name as shown in the UI which does not include the label's name. */
	readableName: string;
};

/**
 * Describes a label which is a collection of partitions. The label is the first part before a > symbol in the
 * partition. e.g., "automated > ui" would create the label "automated" and group all partitions that start with
 * "automated>" into the same label.
 */
export type Label = { label: string; readableName: string; content: Array<Partition | Label> };

/**
 * Used to work with partitions. This is especially useful for partitions which have labels before the actual partition
 * names like 'Label 1>Sub-Label 1>Partition XY' (with {@link LABEL_AND_PARTITION_NAME_SEPARATOR} set to '>').
 */
export class PartitionNameUtils {
	/**
	 * Stores all partitions by the label they belong to.
	 *
	 * For example, imagine having the following partitions: <ul> <li>Partition A</li> <li>Label 1>Partition
	 * B</li><li>Label 1>Sub-Label 1>Partition C</li> </ul> Those would result in the following entries: <ul> <li>'' ->
	 * 'Partition A</li> <li>'Label 1' -> ['Label 1>Partition B', 'Label 1>Sub-Label 1>Partition C']</li> <li>'Label
	 * 1>Sub-Label 1' -> ['Label 1>Sub-Label 1>Partition C']</li> </ul>
	 */
	public partitionsByLabel = new Map<string, string[]>();

	/**
	 * Stores all available sub-labels of a label (direct relations only).
	 *
	 * For example, imagine having the following partitions: <ul> <li>Partition A</li> <li>Label 1>Partition
	 * B</li><li>Label 1>Sub-Label 1>Partition C</li> <li>Label 2>Sub-Label 1>Partition D</li> <li>Label 2>Sub-Label
	 * 2>Partition E</li> </ul> Those would result in the following entries: <ul> <li>'Label 1' -> ['Sub-Label
	 * 1']</li><li>'Label 2'
	 *
	 * - ['Sub-Label 1', 'Sub-Label 2']</li> </ul>
	 */
	public associatedLabels = new Map<string, string[]>();

	/**
	 * Stores the reverse relation of associatedLabels and is used find the key for an entry in associatedLabels without
	 * looping over the whole Map. For example, if <ul> <li>'Label 1' -> ['Sub-Label 1']</li><li>'Label 2' ->
	 * ['Sub-Label 1']</li> </ul> was stored in associatedLabels, then <ul> <li>'Sub-Label 1' -> ['Label 1', 'Label
	 * 2']</li> </ul> would be stored in reverseAssociatedLabels.
	 */
	private reverseAssociatedLabels = new Map<string, string[]>();

	/** Stores all the labels/contents that are not sub-labels of another label. */
	private readonly rootEntries: string[] = [];

	/**
	 * Stores the readable name as value for a full qualified partition/label name. For example, for the full qualified
	 * partition name 'Label 1>Subset 1>Partition Foo' we would store 'Partition Foo', or 'Subset 1' for the
	 * corresponding full qualified label name 'Label 1>Subset 1'.
	 */
	public readableNames = new Map<string, string>();

	/**
	 * Stores an array of objects which have similar information like {@link partitionsByLabel}. We use this instead of
	 * the map because the GoogleClosure templates cannot handle maps yet.
	 */
	public templateInfo: Label[] = [];

	/**
	 * The character which is used to separate labels from each other and to separate the partition name from the
	 * labels.
	 */
	public static readonly LABEL_AND_PARTITION_NAME_SEPARATOR = '>';

	public constructor(partitions: string[]) {
		this.parseInformationFromPartitions(partitions);
		this.prepareTemplateInfo();
	}

	/**
	 * Parses the given partitions to retrieve the necessary information to fill {@link labelsToPartitions},
	 * {@link associatedLabels} and {@link readableNames}.
	 */
	private parseInformationFromPartitions(partitions: string[]): void {
		for (const partition of partitions) {
			const splitResult = partition.split(PartitionNameUtils.LABEL_AND_PARTITION_NAME_SEPARATOR);
			const splitResultLength = splitResult.length;

			const partitionName = splitResult[splitResultLength - 1]!;
			let labels: string[] = [];
			if (splitResultLength > 1) {
				labels = splitResult.slice(0, splitResultLength - 1);
			}
			this.addEntry(partition, labels);
			this.readableNames.set(partition, partitionName);
		}
	}

	/** Adds entries to {@link #associatedLabels} and {@link #labelsToPartitions} for the given partition name and labels. */
	private addEntry(partition: string, labels: string[]): void {
		if (labels.length === 0) {
			this.partitionsByLabel = PartitionNameUtils.addEntryWithoutOverride('', partition, this.partitionsByLabel);
			if (!this.rootEntries.includes('')) {
				this.rootEntries.push('');
			}
			return;
		}
		const partitionContainingLabel = this.addLabelsForEntry(labels);
		// Add the partition only for the label directly containing it
		this.partitionsByLabel = PartitionNameUtils.addEntryWithoutOverride(
			partitionContainingLabel,
			partition,
			this.partitionsByLabel
		);
	}

	/**
	 * Adds the relations for the given stack of labels (resulting from an entry) and the uppermost one as rootEntry.
	 *
	 * @returns The last label, which directly contains the partition.
	 */
	private addLabelsForEntry(labelsStack: string[]): string {
		labelsStack = labelsStack.reverse();
		let previousLabel = '';
		while (labelsStack.length > 0) {
			let currentLabel = labelsStack.pop()!;
			if (previousLabel === '' && !this.rootEntries.includes(currentLabel)) {
				this.rootEntries.push(currentLabel);
			}
			if (!StringUtils.isEmptyOrWhitespace(previousLabel)) {
				currentLabel = previousLabel.concat(
					PartitionNameUtils.LABEL_AND_PARTITION_NAME_SEPARATOR,
					currentLabel
				);
				this.addLabelRelation(previousLabel, currentLabel);
			}
			const readableName =
				currentLabel.split(PartitionNameUtils.LABEL_AND_PARTITION_NAME_SEPARATOR).reverse()[0] ?? currentLabel;
			this.readableNames.set(currentLabel, readableName);
			previousLabel = currentLabel;
		}

		return previousLabel;
	}

	/** Adds entries for the label relation in the associatedLabels-Map and its reverse. */
	private addLabelRelation(parentLabel: string, childLabel: string) {
		this.associatedLabels = PartitionNameUtils.addEntryWithoutOverrideOrDuplicate(
			parentLabel,
			childLabel,
			this.associatedLabels
		);
		this.reverseAssociatedLabels = PartitionNameUtils.addEntryWithoutOverrideOrDuplicate(
			childLabel,
			parentLabel,
			this.reverseAssociatedLabels
		);
	}

	/**
	 * Adds an entry to the given map without overriding the value. If the value is not set yet, it will set its value.
	 * Otherwise, we append the value to the existing list of values, if it is not yet present in the existing list of
	 * values.
	 */
	private static addEntryWithoutOverrideOrDuplicate(
		key: string,
		valueToAdd: string,
		map: Map<string, string[]>
	): Map<string, string[]> {
		const value = map.get(key);
		if (value !== undefined && Array.isArray(value) && value.includes(valueToAdd)) {
			return map;
		}
		return this.addEntryWithoutOverride(key, valueToAdd, map);
	}

	/**
	 * Adds an entry to the given map without overriding the value. If the value is not set yet, it will set its value.
	 * Otherwise, we append the value to the existing list of values.
	 */
	private static addEntryWithoutOverride(
		key: string,
		valueToAdd: string,
		map: Map<string, string[]>
	): Map<string, string[]> {
		let value = map.get(key);
		if (value !== undefined) {
			value.push(valueToAdd);
		} else {
			value = [valueToAdd];
		}
		return map.set(key, value);
	}

	/**
	 * <p>Adds all necessary entries into {@link templateInformation}. We add an entry for every label out of {@link
	 * partitionsByLabel}, except for those labels which do not have any partitions assigned directly. Partitions are
	 * considered as directly assigned, if they do not belong to a sub-label of the label.</p> <p>For example: <ul>
	 * <li>Foo>Partition Bar is directly assigned to the label Foo</li> <li>Foo>Bar>Partition is not directly assigned
	 * to the label Foo, but to Foo>Bar</li> </ul> </p>
	 */
	private prepareTemplateInfo(): void {
		this.templateInfo = this.rootEntries.map((content: string) => this.prepareTemplateInfoForContent(content));

		this.templateInfo = this.templateInfo.sort((firstInfo, secondInfo) =>
			firstInfo.label.localeCompare(secondInfo.label)
		);
	}

	/**
	 * Returns the Label object for this specific label, including Partition objects for directly contained partitions
	 * and recursively acquired Label objects for associated labels.
	 *
	 * @param content: Name of the targeted label - ('' for the root)
	 * @returns The label with its contents for the templateInfo
	 */
	private prepareTemplateInfoForContent(content: string): Label {
		const contents: Array<Partition | Label> = [];
		for (const partition of this.partitionsByLabel.get(content) ?? []) {
			const readableName = this.readableNames.get(partition)!;
			contents.push({ partition, readableName });
		}

		for (const associatedLabel of this.associatedLabels.get(content) ?? []) {
			contents.push(this.prepareTemplateInfoForContent(associatedLabel));
		}

		const readableName = this.readableNames.get(content) ?? content;

		return { label: content, readableName, content: contents };
	}

	/**
	 * Returns all labels whose partitions are selected.
	 *
	 * @param selectedPartitions The selected partitions
	 * @param filterRedundant Optional parameter which indicates whether this method should also return sub-labels of
	 *   selected labels
	 */
	public getSelectedLabels(selectedPartitions: Set<string>, filterRedundant = false): string[] {
		const labelWorkset: string[] = [];
		this.partitionsByLabel.forEach((partitions: string[], label: string | null) => {
			const includedLabel = label !== null && label.length > 0;
			if (includedLabel && partitions.every(partition => selectedPartitions.has(partition))) {
				labelWorkset.push(label);
			}
		});

		let selectedLabels: string[] = this.getParentLabelsForSet(labelWorkset);

		selectedLabels = selectedLabels.sort();
		if (filterRedundant && selectedLabels.length > 0) {
			const filteredSelectedLabels = [selectedLabels[0]!];
			let prefixToRemove = selectedLabels[0]!;
			for (let i = 1; i < selectedLabels.length; i++) {
				const currentLabel = selectedLabels[i]!;
				if (!currentLabel.startsWith(prefixToRemove)) {
					filteredSelectedLabels.push(currentLabel);
					prefixToRemove = currentLabel;
				}
			}
			selectedLabels = filteredSelectedLabels;
		}
		return ArrayUtils.removeDuplicates(selectedLabels);
	}

	/** Recursively collect all parent labels for the given set "up" to root labels, including the given set. */
	private getParentLabelsForSet(labelWorkset: string[]): string[] {
		const finishedLabels: string[] = [];

		while (labelWorkset.length > 0) {
			const currentWorkingLabel: string = labelWorkset.pop()!;
			finishedLabels.push(currentWorkingLabel);
			const parentLabels: string[] = this.reverseAssociatedLabels.get(currentWorkingLabel) ?? [];
			for (const parentLabel of parentLabels) {
				if (!(parentLabel in labelWorkset) || !(parentLabel in finishedLabels)) {
					labelWorkset.push(parentLabel);
				}
			}
		}

		return finishedLabels;
	}

	/** Returns all partitions which have the given partition label. */
	public getIncludedPartitions(partitionLabel: string): string[] {
		const includedPartitions: string[] = [];
		const nestedLabels = [partitionLabel];
		while (nestedLabels.length > 0) {
			const currentLabel = nestedLabels.pop()!;
			const partitionsOfLabel = this.partitionsByLabel.get(currentLabel);
			if (partitionsOfLabel !== undefined) {
				ArrayUtils.addAllToArray(includedPartitions, partitionsOfLabel);
			}
			const subLabels = this.associatedLabels.get(currentLabel);
			if (subLabels !== undefined) {
				ArrayUtils.addAllToArray(nestedLabels, subLabels);
				subLabels.forEach((label: string) =>
					ArrayUtils.addAllToArray(includedPartitions, this.getIncludedPartitions(label))
				);
			}
		}
		return ArrayUtils.removeDuplicates(includedPartitions);
	}

	/** Removes the leading and trailing white space for each label and the partition name itself. */
	public static trimPartitionName(partition: string): string {
		const splitResult = partition.split(PartitionNameUtils.LABEL_AND_PARTITION_NAME_SEPARATOR);
		const partitionName = splitResult[splitResult.length - 1]!;
		const labels = splitResult.slice(0, splitResult.length - 1);
		let result = '';
		for (const label of labels) {
			result = result.concat(label.trim(), PartitionNameUtils.LABEL_AND_PARTITION_NAME_SEPARATOR);
		}
		return result.concat(partitionName.trim());
	}
}
